/**
 * Standard enthalpies of formation, ΔfH° in kJ/mol at 298.15 K (25 °C) and 1 bar.
 *
 * Source: CRC Handbook of Chemistry and Physics, 84th ed. (2004), "Standard Thermodynamic
 * Properties of Chemical Substances", as tabulated in LibreTexts "T1: Standard Thermodynamic
 * Quantities" (https://chem.libretexts.org/Ancillary_Materials/Reference/Reference_Tables/Thermodynamics_Tables/T1:_Standard_Thermodynamic_Quantities).
 * Each value below was read from that table; the elements in their reference states (and
 * H⁺(aq), by convention) are 0. Other handbooks differ in the last digit or two — the NBS
 * tables give NO(g) 90.25 and N₂O₄(g) 9.16, for instance, where the CRC edition gives 91.3 and
 * 11.1 — so a result is only as precise as the table behind it.
 *
 * Each row is [formula, state, name, ΔfH°]. States: g gas, l liquid, s solid, aq aqueous
 * (infinite dilution), and "graphite"/"diamond" for the two forms of carbon. `C(s)` and
 * `S(s)` mean graphite and rhombic sulfur, `CaCO₃(s)` means calcite.
 */

export type State = "g" | "l" | "s" | "aq" | "graphite" | "diamond";

type Row = readonly [string, State, string, number];

const ROWS: readonly Row[] = [
  // Elements in their reference states, and a few other forms
  ["H2", "g", "Hydrogen", 0],
  ["O2", "g", "Oxygen", 0],
  ["N2", "g", "Nitrogen", 0],
  ["F2", "g", "Fluorine", 0],
  ["Cl2", "g", "Chlorine", 0],
  ["Br2", "l", "Bromine", 0],
  ["I2", "s", "Iodine", 0],
  ["C", "graphite", "Carbon (graphite)", 0],
  ["S", "s", "Sulfur (rhombic)", 0],
  ["P", "s", "Phosphorus (white)", 0],
  ["Na", "s", "Sodium", 0],
  ["K", "s", "Potassium", 0],
  ["Mg", "s", "Magnesium", 0],
  ["Ca", "s", "Calcium", 0],
  ["Al", "s", "Aluminum", 0],
  ["Fe", "s", "Iron", 0],
  ["Cu", "s", "Copper", 0],
  ["Zn", "s", "Zinc", 0],
  ["Ag", "s", "Silver", 0],
  ["C", "diamond", "Carbon (diamond)", 1.9],
  ["O3", "g", "Ozone", 142.7],
  ["Br2", "g", "Bromine", 30.9],
  ["I2", "g", "Iodine", 62.4],
  // Hydrogen compounds
  ["H2O", "l", "Water", -285.8],
  ["H2O", "g", "Water", -241.8],
  ["H2O2", "l", "Hydrogen peroxide", -187.8],
  ["HF", "g", "Hydrogen fluoride", -273.3],
  ["HCl", "g", "Hydrogen chloride", -92.3],
  ["HBr", "g", "Hydrogen bromide", -36.3],
  ["HI", "g", "Hydrogen iodide", 26.5],
  ["H2S", "g", "Hydrogen sulfide", -20.6],
  ["NH3", "g", "Ammonia", -45.9],
  ["N2H4", "l", "Hydrazine", 50.6],
  ["PH3", "g", "Phosphine", 5.4],
  // Carbon compounds
  ["CO", "g", "Carbon monoxide", -110.5],
  ["CO2", "g", "Carbon dioxide", -393.5],
  ["CH4", "g", "Methane", -74.6],
  ["C2H6", "g", "Ethane", -84],
  ["C3H8", "g", "Propane", -103.8],
  ["C2H4", "g", "Ethene (ethylene)", 52.4],
  ["C2H2", "g", "Ethyne (acetylene)", 227.4],
  ["C6H6", "l", "Benzene", 49.1],
  ["CH3OH", "l", "Methanol", -239.2],
  ["CH3OH", "g", "Methanol", -201],
  ["C2H5OH", "l", "Ethanol", -277.6],
  ["C2H5OH", "g", "Ethanol", -234.8],
  ["CH3COOH", "l", "Acetic acid", -484.3],
  ["CCl4", "l", "Carbon tetrachloride", -128.2],
  ["CHCl3", "l", "Chloroform", -134.1],
  ["CH3Cl", "g", "Chloromethane", -81.9],
  ["CS2", "l", "Carbon disulfide", 89],
  ["HCN", "g", "Hydrogen cyanide", 135.1],
  ["C6H12O6", "s", "Glucose", -1273.3],
  // Nitrogen, sulfur and phosphorus compounds
  ["NO", "g", "Nitrogen monoxide", 91.3],
  ["NO2", "g", "Nitrogen dioxide", 33.2],
  ["N2O", "g", "Dinitrogen monoxide", 81.6],
  ["N2O4", "g", "Dinitrogen tetroxide", 11.1],
  ["SO2", "g", "Sulfur dioxide", -296.8],
  ["SO3", "g", "Sulfur trioxide", -395.7],
  ["HNO3", "l", "Nitric acid", -174.1],
  ["H3PO4", "s", "Phosphoric acid", -1284.4],
  ["PCl3", "l", "Phosphorus trichloride", -319.7],
  ["PCl5", "g", "Phosphorus pentachloride", -374.9],
  // Oxides and hydroxides
  ["Na2O", "s", "Sodium oxide", -414.2],
  ["MgO", "s", "Magnesium oxide", -601.6],
  ["CaO", "s", "Calcium oxide", -634.9],
  ["BaO", "s", "Barium oxide", -548],
  ["Al2O3", "s", "Aluminum oxide", -1675.7],
  ["FeO", "s", "Iron(II) oxide", -272],
  ["Fe2O3", "s", "Iron(III) oxide", -824.2],
  ["Fe3O4", "s", "Iron(II,III) oxide", -1118.4],
  ["CuO", "s", "Copper(II) oxide", -157.3],
  ["Cu2O", "s", "Copper(I) oxide", -168.6],
  ["TiO2", "s", "Titanium dioxide", -944],
  ["PbO2", "s", "Lead(IV) oxide", -277.4],
  ["Mg(OH)2", "s", "Magnesium hydroxide", -924.5],
  ["Ca(OH)2", "s", "Calcium hydroxide", -985.2],
  // Salts
  ["NaF", "s", "Sodium fluoride", -576.6],
  ["NaCl", "s", "Sodium chloride", -411.2],
  ["NaBr", "s", "Sodium bromide", -361.1],
  ["KCl", "s", "Potassium chloride", -436.5],
  ["KBr", "s", "Potassium bromide", -393.8],
  ["MgCl2", "s", "Magnesium chloride", -641.3],
  ["CaCl2", "s", "Calcium chloride", -795.4],
  ["CaF2", "s", "Calcium fluoride", -1228],
  ["AlCl3", "s", "Aluminum chloride", -704.2],
  ["AgCl", "s", "Silver chloride", -127],
  ["AgBr", "s", "Silver bromide", -100.4],
  ["ZnCl2", "s", "Zinc chloride", -415.1],
  ["CuCl2", "s", "Copper(II) chloride", -220.1],
  ["PbCl2", "s", "Lead(II) chloride", -359.4],
  ["NH4Cl", "s", "Ammonium chloride", -314.4],
  ["NH4NO3", "s", "Ammonium nitrate", -365.6],
  ["NaNO3", "s", "Sodium nitrate", -467.9],
  ["KNO3", "s", "Potassium nitrate", -494.6],
  ["AgNO3", "s", "Silver nitrate", -124.4],
  ["Na2CO3", "s", "Sodium carbonate", -1130.7],
  ["K2CO3", "s", "Potassium carbonate", -1151],
  ["CaCO3", "s", "Calcium carbonate (calcite)", -1207.6],
  ["BaCO3", "s", "Barium carbonate", -1213],
  ["Na2SO4", "s", "Sodium sulfate", -1387.1],
  ["K2SO4", "s", "Potassium sulfate", -1437.8],
  ["CaSO4", "s", "Calcium sulfate", -1434.5],
  ["BaSO4", "s", "Barium sulfate", -1473.2],
  ["(NH4)2SO4", "s", "Ammonium sulfate", -1180.9],
  ["ZnS", "s", "Zinc sulfide (sphalerite)", -206],
  ["PbS", "s", "Lead(II) sulfide", -100.4],
  ["CuS", "s", "Copper(II) sulfide", -53.1],
  // Aqueous ions and solutions
  ["H+", "aq", "Hydrogen ion", 0],
  ["OH-", "aq", "Hydroxide ion", -230],
  ["Na+", "aq", "Sodium ion", -240.1],
  ["NH4+", "aq", "Ammonium ion", -132.5],
  ["Mg^2+", "aq", "Magnesium ion", -466.85],
  ["Fe^2+", "aq", "Iron(II) ion", -89.1],
  ["Fe^3+", "aq", "Iron(III) ion", -48.5],
  ["Ag+", "aq", "Silver ion", 105.6],
  ["F-", "aq", "Fluoride ion", -332.6],
  ["Cl-", "aq", "Chloride ion", -167.2],
  ["Br-", "aq", "Bromide ion", -121.6],
  ["I-", "aq", "Iodide ion", -55.2],
  ["SO4^2-", "aq", "Sulfate ion", -909.3],
  ["CH3COO-", "aq", "Acetate ion", -486],
  ["HNO3", "aq", "Nitric acid", -207.4],
  ["H2SO4", "aq", "Sulfuric acid", -909.3],
  ["NaF", "aq", "Sodium fluoride", -572.8],
  ["NaCl", "aq", "Sodium chloride", -407.3],
  ["NaBr", "aq", "Sodium bromide", -361.7],
  ["NaNO3", "aq", "Sodium nitrate", -447.5],
  ["Pb(NO3)2", "aq", "Lead(II) nitrate", -416.3],
];

export interface Species {
  /** Formula as typed in the source rows, e.g. "SO4^2-". */
  raw: string;
  state: State;
  name: string;
  /** kJ/mol */
  dHf: number;
}

export const SPECIES: readonly Species[] = ROWS.map(
  ([raw, state, name, dHf]) => ({ raw, state, name, dHf })
);

export const SOURCE_NAME =
  "CRC Handbook of Chemistry and Physics, 84th ed. (via LibreTexts T1)";
